import { Phone, ArrowRight, Check, Quote, Clock, MapPin, Wrench, Droplet } from "lucide-react";
import ConceptHeader from "./ConceptHeader";
import { ConceptFooter } from "./LayoutServicePro";

/**
 * "Emergency Action" layout — light cream, urgency-first.
 * Used by Precision Plumbing.
 * Distinct features: red emergency strip up top, hero is dominated by a
 * GIANT phone-number CTA (rather than a form), transparent flat-rate
 * pricing TABLE, service-area map mock.
 */
export default function LayoutEmergencyAction({ c }) {
  const t = c.theme;
  return (
    <div style={{ background: t.bg, color: t.text, fontFamily: t.fontBody, minHeight: "100vh" }}>
      <ConceptHeader industry={c.industry} />

      {/* Emergency strip */}
      <div className="border-b" style={{ background: t.accent, borderColor: t.accent }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-2.5 flex items-center justify-center md:justify-between text-[13px] flex-wrap gap-3" style={{ color: "#fff" }}>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"/>
            24/7 EMERGENCY PLUMBING — TECHS DISPATCHED IN UNDER 60 MIN
          </span>
          <span className="font-medium flex items-center gap-2">
            <Phone size={14}/> {c.phone}
          </span>
        </div>
      </div>

      {/* Faux site nav */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full" style={{ background: t.accent, color: "#fff" }}>
            <Droplet size={18}/>
          </span>
          <p style={{ fontFamily: t.fontDisplay, fontSize: 24, fontWeight: 600 }}>{c.name}</p>
        </div>
        <nav className="hidden md:flex items-center gap-7 text-[13px]" style={{ color: t.muted }}>
          <span>Services</span><span>Pricing</span><span>Service Area</span><span>About</span>
        </nav>
      </div>

      {/* HERO — giant phone CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 md:pb-28 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-[0.18em]"
              style={{ background: t.accentSoft, color: t.accent }}>
          <Clock size={12}/> {c.hero.eyebrow.split(" · ")[0]}
        </span>
        <h1 className="mt-6 mx-auto max-w-4xl leading-[1.02]"
            style={{ fontFamily: t.fontDisplay, fontSize: "clamp(44px, 7vw, 92px)", fontWeight: 700 }}>
          {c.hero.title}
        </h1>
        <p className="mt-7 mx-auto max-w-2xl text-lg md:text-xl leading-relaxed" style={{ color: t.muted }}>
          {c.hero.sub}
        </p>

        {/* GIANT phone CTA */}
        <div className="mt-12 inline-flex flex-col items-center">
          <p className="text-[12px] uppercase tracking-[0.28em]" style={{ color: t.muted }}>Call now — answered live</p>
          <a href={`tel:${c.phone.replace(/\D/g,'')}`} className="mt-3 inline-flex items-center gap-4 px-8 py-5 rounded-full shadow-lg transition-transform hover:scale-[1.03]"
             style={{ background: t.accent, color: "#fff" }}>
            <Phone size={32}/>
            <span style={{ fontFamily: t.fontDisplay, fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 700, letterSpacing: "-0.02em" }}>
              {c.phone}
            </span>
          </a>
          <div className="mt-4 flex items-center gap-4 text-[13px]" style={{ color: t.muted }}>
            <span>Or</span>
            <span className="underline">Book online ↗</span>
            <span>·</span>
            <span>Avg dispatch: 60 min</span>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="py-10 border-y" style={{ background: t.surface, borderColor: t.muted + "22" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {c.stats.map((s) => (
            <div key={s.v} className="text-center">
              <p style={{ fontFamily: t.fontDisplay, fontSize: 32, color: t.accent, fontWeight: 700 }}>{s.k}</p>
              <p className="mt-1 text-[12px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES — 4 icons row */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Services</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 700 }}>
              Every plumbing job, done right.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.services.map((s, i) => (
              <div key={s.title} className="p-7 rounded-2xl border text-center" style={{ background: "#fff", borderColor: t.muted + "22" }}>
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full"
                      style={{ background: t.accentSoft, color: t.accent }}>
                  <Wrench size={22}/>
                </span>
                <h3 className="mt-5 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: 20, fontWeight: 700 }}>{s.title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: t.muted }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLAT-RATE PRICING TABLE */}
      <section className="py-20 md:py-24" style={{ background: t.surface }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Transparent pricing</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 700 }}>
              Up-front, flat-rate pricing.
            </h2>
            <p className="mt-5 text-lg" style={{ color: t.muted }}>Quoted before we start. No surprises — ever.</p>
          </div>
          <div className="rounded-2xl border overflow-hidden" style={{ background: t.bg, borderColor: t.muted + "22" }}>
            {c.flatRates.map((r, i) => (
              <div key={r.service} className="flex items-center justify-between px-7 py-5"
                   style={{ borderTop: i === 0 ? "none" : `1px solid ${t.muted}22` }}>
                <span className="text-[16px]">{r.service}</span>
                <span style={{ fontFamily: t.fontDisplay, fontSize: 22, fontWeight: 700, color: t.accent }}>{r.price}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-[13px]" style={{ color: t.muted }}>
            *Includes diagnostic, parts, and labor. Complex jobs quoted separately.
          </p>
        </div>
      </section>

      {/* SERVICE AREA + TESTIMONIAL */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="text-[12px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Service area</p>
            <h2 className="mt-4 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(28px, 3.6vw, 44px)", fontWeight: 700 }}>
              Serving the whole metro — fast.
            </h2>
            <ul className="mt-7 grid grid-cols-2 gap-2 text-[14px]">
              {c.valueProps.map((v) => (
                <li key={v} className="flex items-center gap-2"><Check size={14} style={{ color: t.accent }}/>{v}</li>
              ))}
            </ul>
            <a href={`tel:${c.phone.replace(/\D/g,'')}`} className="mt-9 inline-flex items-center gap-3 px-7 py-4 rounded-full font-medium text-[15px]" style={{ background: t.accent, color: "#fff" }}>
              <Phone size={16}/> Call {c.phone}
            </a>
          </div>
          <div className="lg:col-span-6">
            {/* faux map */}
            <div className="aspect-[4/3] rounded-2xl border relative overflow-hidden"
                 style={{
                   backgroundColor: t.accentSoft,
                   backgroundImage: `url(${c.images.hero})`,
                   backgroundSize: "cover",
                   backgroundPosition: "center",
                   borderColor: t.muted + "22",
                 }}>
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${t.bg}AA 0%, ${t.bg}33 100%)` }}/>
              {/* pins */}
              {[[20,30],[55,40],[35,65],[70,55],[45,25]].map(([x,y], i) => (
                <span key={i} className="absolute" style={{ left:`${x}%`, top:`${y}%` }}>
                  <span className="block w-3 h-3 rounded-full ring-4 ring-white" style={{ background: t.accent, boxShadow: `0 0 0 8px ${t.accent}33` }}/>
                </span>
              ))}
              <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-white flex items-center gap-3"
                   style={{ borderColor: t.muted + "22" }}>
                <MapPin size={16} style={{ color: t.accent }}/>
                <span className="text-[13px]">Greater metro · 30-mile radius · 7 trucks dispatched today</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20" style={{ background: t.surface }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Quote size={32} style={{ color: t.accent }} className="mx-auto"/>
          <p className="mt-6 leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(24px, 3.2vw, 38px)", fontWeight: 700 }}>
            "{c.testimonial.quote}"
          </p>
          <p className="mt-6 text-[13px] uppercase tracking-[0.22em]" style={{ color: t.muted }}>— {c.testimonial.author}</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20" style={{ background: t.accent, color: "#fff" }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="leading-tight" style={{ fontFamily: t.fontDisplay, fontSize: "clamp(32px, 4.5vw, 56px)", fontWeight: 700 }}>
            {c.finalCta}.
          </h2>
          <a href={`tel:${c.phone.replace(/\D/g,'')}`} className="mt-8 inline-flex items-center gap-3 px-9 py-4 rounded-full font-medium text-[16px] bg-white" style={{ color: t.accent }}>
            <Phone size={18}/> {c.phone}
          </a>
        </div>
      </section>

      <ConceptFooter accent={t.accent} muted={t.muted} bg={t.bg}/>
    </div>
  );
}
